import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';

/**
 * Maps a push notification data `type` field to the correct in-app route.
 * Mirrors the logic in NotificationCenter's `getTargetPath`. All known
 * loan/item notification types route to `/my-items`; unknown types
 * fall back to `/dashboard`.
 *
 * @param type - The notification type string from the push payload.
 * @returns The React Router path to navigate to.
 */
function getTargetPath(type: string | undefined, itemId: string | undefined): string {
  const itemParam = itemId ? `?item=${itemId}` : '';

  switch (type) {
    case 'loan_request_received':
    case 'loan_request_approved':
    case 'loan_request_rejected':
    case 'loan_return_completed':
    case 'loan_return_initiated':
    case 'item_marked_overdue':
    case 'loan_due_date_set':
    case 'loan_proposal_received':
    case 'loan_proposal_accepted':
    case 'loan_proposal_declined':
    case 'loan_proposal_withdrawn':
    case 'loan_due_soon':
    case 'loan_nudge':
      return `/my-items${itemParam}`;
    default:
      return '/dashboard';
  }
}

/**
 * Upserts a device token into the `device_tokens` table. On conflict
 * (same token string), only `updated_at` is refreshed via the DB trigger.
 * Determines platform (`ios` or `android`) from `Capacitor.getPlatform()`.
 * Fails silently (logs to console) — callers should not depend on success.
 *
 * @param token - The FCM/APNs device token to store.
 */
async function upsertDeviceToken(token: string) {
  try {
    const platform = Capacitor.getPlatform(); // 'ios' or 'android'
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) return;

    const { error } = await supabase
      .from('device_tokens')
      .upsert({ user_id: user.id, token, platform }, { onConflict: 'token' });

    if (error) {
      console.error('Failed to upsert device token:', error);
    } else {
      console.log('Device token upserted successfully');
    }
  } catch (err) {
    console.error('Failed to upsert device token:', err);
  }
}

/**
 * Sole owner of all Capacitor push notification listeners. Must be mounted
 * exactly once in the app (via PushNotificationProvider) so that foreground
 * toasts, tap-to-navigate, token refresh, and registration error handling
 * cover the entire authenticated session.
 *
 * Does **not** register on mount. Call `registerForPushNotifications()` (from
 * the returned object) or the standalone `promptPushRegistrationIfNeeded()`
 * to trigger FCM registration. Those functions only request permission and
 * call `register()`; the `registration` listener attached here handles the
 * resulting token upsert.
 *
 * On web this is a complete no-op — all methods resolve immediately.
 *
 * @returns `{ registerForPushNotifications, deviceToken }` where
 *   `registerForPushNotifications` requests permission and registers with FCM,
 *   and `deviceToken` is the current FCM token (null until registration succeeds).
 */
export function usePushNotifications() {
  const [deviceToken, setDeviceToken] = useState<string | null>(null);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  // Guard: skip re-attaching duplicate event listeners on effect re-run.
  const listenersAttached = useRef(false);

  // Attach all four listeners once (idempotent via ref guard).
  // This is the single attachment point — no other code should call addListener.
  useEffect(() => {
    // Push only works on iOS/Android
    if (!Capacitor.isNativePlatform() || listenersAttached.current) return;
    listenersAttached.current = true;

    // Foreground notification — show a toast and refresh all relevant caches.
    // Mirrors the invalidation set in useNotificationRealtime so the UI stays
    // consistent even if the Realtime WebSocket connection is down.
    PushNotifications.addListener('pushNotificationReceived', (notification) => {
      toast(notification.title ?? 'New notification', {
        description: notification.body,
      });
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.invalidateQueries({ queryKey: ['loan-requests'] });
      queryClient.invalidateQueries({ queryKey: ['my-borrowed-items'] });
      queryClient.invalidateQueries({ queryKey: ['items'] });
      queryClient.invalidateQueries({ queryKey: ['my-items'] });
    });

    // User tapped a notification — navigate to the relevant screen,
    // including ?item= so the target page can highlight the relevant item.
    PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
      const data = action.notification.data as Record<string, string> | undefined;
      navigate(getTargetPath(data?.type, data?.item_id));
    });

    // Token refresh — FCM can silently rotate tokens; re-upsert when it does.
    // Also fires after PushNotifications.register() completes successfully.
    PushNotifications.addListener('registration', (token) => {
      console.log('FCM token:', token.value);
      setDeviceToken(token.value);
      upsertDeviceToken(token.value);
    });

    // Registration failure — surface errors regardless of how registration was triggered.
    PushNotifications.addListener('registrationError', (error) => {
      console.error('Push registration error:', error);
      toast.error('Failed to register for push notifications.', {
        description: error.error,
      });
    });

    return () => {
      PushNotifications.removeAllListeners();
    };
  }, [navigate, queryClient]);

  const registerForPushNotifications = useCallback(async () => {
    if (!Capacitor.isNativePlatform()) return;

    const permResult = await PushNotifications.requestPermissions();

    if (permResult.receive !== 'granted') {
      toast.error('Push notification permission was denied.');
      return;
    }

    // Listeners are already attached by the useEffect above — just register.
    await PushNotifications.register();
  }, []);

  return { registerForPushNotifications, deviceToken };
}

/**
 * Checks whether the current user already has device tokens registered. If
 * none exist, requests push permission and calls `PushNotifications.register()`.
 *
 * Does **not** attach listeners — `usePushNotifications` owns all listeners.
 * This function only triggers `requestPermissions()` and `register()`; the
 * hook's `registration` listener handles the resulting token upsert.
 *
 * Designed to be called fire-and-forget (e.g. from `useCreateLoan` `onSuccess`)
 * so the iOS / Android 13+ permission prompt fires after the user's first loan,
 * not on app launch. Silent on failure — this is a background prompt, not
 * user-initiated. On Android API < 33, permission is auto-granted (no dialogue).
 *
 * @returns Resolves when registration completes or is skipped. No-op on web.
 */
export async function promptPushRegistrationIfNeeded(): Promise<void> {
  if (!Capacitor.isNativePlatform()) return;

  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) return;

  // Check if user already has device tokens
  const { count } = await supabase
    .from('device_tokens')
    .select('*', { count: 'exact', head: true })
    .eq('user_id', user.id);

  if (count && count > 0) return;

  // Request permission — on Android < 33 this resolves immediately as 'granted'
  const permResult = await PushNotifications.requestPermissions();
  if (permResult.receive !== 'granted') return;

  // Listeners are already attached by usePushNotifications — just register.
  await PushNotifications.register();
}
